'use client';
import { useFloating, autoUpdate, offset, flip, shift, limitShift, arrow } from '@floating-ui/react-dom';
import { transform } from 'css-calc-transform';
import React, { useState, useEffect, useRef } from 'react';
import { ANIMATION_STATUS } from '../../constants/tooltip.mjs';
import { mergeRefs } from '../../utils/ref.mjs';

const ANCHOR_SIZE = 7;
const TOOLTIP_PLACEMENT_MARGIN = ANCHOR_SIZE / 2;
/** 툴팁 컨텐츠 위치, 화살표 위치 등을 계산하기 위한 useFloating의 middleware를 반환합니다. */
function getTooltipFloatingMiddlewares(_ref) {
  let {
    tooltipPosition: {
      mainAxis,
      crossAxis,
      overflowMainAxis,
      overflowCrossAxis
    } = {},
    arrowPosition,
    $arrow
  } = _ref;
  const getIsYAxis = placement => ['left', 'right'].includes(placement);
  const overflowCrossAxisOption = typeof overflowCrossAxis === 'string' ? {
    type: overflowCrossAxis
  } : overflowCrossAxis;
  /** 툴팁 꼬리 위치를 픽셀 단위로 계산하여, 이후 미들웨어에 전달합니다. */
  const calculateArrowPositionPixelMiddleware = {
    name: 'meta',
    fn: state => {
      const isYAxis = getIsYAxis(state.placement);
      const arrowPositionPixel = transform({
        value: `calc(${arrowPosition})`,
        prop: isYAxis ? 'height' : 'width',
        parent: {
          width: state.rects.floating.width,
          height: state.rects.floating.height
        }
      });
      return {
        data: {
          arrowPositionPixel
        }
      };
    },
    options: [arrowPosition]
  };
  /** 툴팁 컨텐츠를 offset만큼 이동시킵니다. */
  const moveTooltipPositionMiddleware = {
    ...offset(state => {
      var _state$middlewareData;
      const isYAxis = getIsYAxis(state.placement);
      const arrowPositionPixel = ((_state$middlewareData = state.middlewareData.meta) === null || _state$middlewareData === void 0 ? void 0 : _state$middlewareData.arrowPositionPixel) ?? 0;
      const {
        width,
        height
      } = state.rects.floating;
      const contentCenterFixedArrowOffset = (isYAxis ? height : width) / 2 - arrowPositionPixel;
      return {
        mainAxis: (mainAxis ?? 0) + TOOLTIP_PLACEMENT_MARGIN,
        crossAxis: (crossAxis ?? 0) + (contentCenterFixedArrowOffset ?? 0)
      };
    }),
    options: [mainAxis, crossAxis]
  };
  /**
   * [툴팁 컨텐츠가 mainAxis 축 기준으로 화면을 벗어날 경우]
   * flip 미들웨어를 적용하여 placement를 거꾸로 적용합니다.
   */
  const flipTooltipPlacementMiddlewareInOverflowMainAxis = flip();
  /**
   * [툴팁 컨텐츠가 crossAxis 축 기준으로 화면을 벗어날 경우]
   * shift 미들웨어를 적용하여 화면 안으로 이동시킵니다.
   */
  const shiftTooltipPositionMiddlewareInOverflowCrossAxis = shift({
    padding: overflowCrossAxisOption === null || overflowCrossAxisOption === void 0 ? void 0 : overflowCrossAxisOption.padding,
    limiter: limitShift({
      offset: state => {
        const isYAxis = getIsYAxis(state.placement);
        const {
          reference,
          floating
        } = state.rects;
        // baseComponent가 화면을 넘어가면 더 이상 shift하지 않음
        return isYAxis ? Math.min(reference.height, floating.height) : Math.min(reference.width, floating.width);
      }
    })
  });
  /**
   * [툴팁 컨텐츠가 crossAxis 축 기준으로 화면을 벗어날 경우]
   * shift 미들웨어를 적용하여 툴팁 컨텐츠가 자동으로 이동할 때, 툴팁 꼬리는 움직이지 않도록 고정시키는 위치 style을 계산합니다.
   */
  const centerArrowPositionMiddlewareInOverflowCrossAxis = arrow({
    element: $arrow
  });
  /** 툴팁 꼬리 위치 style을 계산합니다. */
  const arrowPositionMiddleware = {
    name: 'arrow',
    fn: state => {
      var _state$middlewareData2;
      const isYAxis = getIsYAxis(state.placement);
      const arrowPositionPixel = ((_state$middlewareData2 = state.middlewareData.meta) === null || _state$middlewareData2 === void 0 ? void 0 : _state$middlewareData2.arrowPositionPixel) ?? 0;
      return {
        data: {
          [isYAxis ? 'y' : 'x']: arrowPositionPixel - ANCHOR_SIZE / 2
        }
      };
    }
  };
  /** 툴팁 꼬리 위치에 따라 transition의 transformOrigin style을 계산합니다. */
  const transformOriginMiddleware = {
    name: 'transition',
    fn: state => {
      var _state$middlewareData3, _state$middlewareData4;
      const x = (((_state$middlewareData3 = state.middlewareData.arrow) === null || _state$middlewareData3 === void 0 ? void 0 : _state$middlewareData3.x) ?? 0) + ANCHOR_SIZE / 2;
      const y = (((_state$middlewareData4 = state.middlewareData.arrow) === null || _state$middlewareData4 === void 0 ? void 0 : _state$middlewareData4.y) ?? 0) + ANCHOR_SIZE / 2;
      const transformOrigin = {
        left: `100% ${y}px`,
        right: `0% ${y}px`,
        top: `${x}px 100%`,
        bottom: `${x}px 0%`
      }[state.placement];
      return {
        data: {
          transformOrigin
        }
      };
    }
  };
  return [calculateArrowPositionPixelMiddleware, moveTooltipPositionMiddleware, (overflowCrossAxisOption === null || overflowCrossAxisOption === void 0 ? void 0 : overflowCrossAxisOption.type) === 'shift' ? shiftTooltipPositionMiddlewareInOverflowCrossAxis : null, overflowMainAxis === 'flip' ? flipTooltipPlacementMiddlewareInOverflowMainAxis : null, (overflowCrossAxisOption === null || overflowCrossAxisOption === void 0 ? void 0 : overflowCrossAxisOption.type) === 'shift' ? centerArrowPositionMiddlewareInOverflowCrossAxis : arrowPositionMiddleware, transformOriginMiddleware];
}
function TooltipCore(_ref2) {
  var _middlewareData$trans, _middlewareData$arrow, _middlewareData$arrow2;
  let {
    isShow,
    id = `tooltip__${Math.random().toString(36).substring(2)}`,
    hasAnimation,
    initialPlacement,
    tooltipPosition,
    arrowPosition,
    child,
    baseElement,
    renderTooltipComponent
  } = _ref2;
  /** tooltip animation */
  const [isRunning, setIsRunning] = useState(false);
  const [animationStatus, setAnimationStatus] = useState(ANIMATION_STATUS.LEAVE);
  const handleTransitionEnd = () => {
    if (animationStatus === ANIMATION_STATUS.LEAVE) {
      setIsRunning(false);
    }
  };
  useEffect(() => {
    if (isShow) {
      if (hasAnimation) {
        requestAnimationFrame(() => setAnimationStatus(ANIMATION_STATUS.ENTER));
      }
      setIsRunning(true);
    } else {
      if (hasAnimation) {
        requestAnimationFrame(() => setAnimationStatus(ANIMATION_STATUS.LEAVE));
      } else {
        setIsRunning(false);
      }
    }
  }, [isShow, hasAnimation]);
  const $tooltip = useRef(null);
  const $child = useRef(null);
  const $arrow = useRef(null);
  /** tooltip position style */
  const {
    refs,
    floatingStyles,
    middlewareData,
    placement: finalPlacement
  } = useFloating({
    placement: initialPlacement,
    open: isRunning,
    transform: false,
    elements: {
      reference: baseElement // baseElement를 사용하는 경우 - useFloating 훅에 DOM Element를 직접 전달합니다.
    },
    middleware: getTooltipFloatingMiddlewares({
      tooltipPosition,
      arrowPosition,
      $arrow
    }),
    whileElementsMounted: autoUpdate
  });
  // child를 사용하는 경우 - DOM Element를 전달하기 위해 child에 refs.setReference를 주입합니다.
  const childWithMergedRefs = child && /*#__PURE__*/React.cloneElement(child, {
    ...child.props,
    ref: mergeRefs([child.ref, $child, refs.setReference])
  });
  // 접근성 요소 동적 추가
  useEffect(() => {
    const $element = child ? $child.current : baseElement;
    if (!$element) {
      return;
    }
    $element.setAttribute('aria-describedby', id);
  }, [child, baseElement, id]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, childWithMergedRefs, isRunning && renderTooltipComponent({
    $tooltip: mergeRefs([$tooltip, refs.setFloating]),
    $arrow,
    id,
    finalPlacement: finalPlacement,
    contentStyle: {
      ...floatingStyles,
      transformOrigin: (_middlewareData$trans = middlewareData.transition) === null || _middlewareData$trans === void 0 ? void 0 : _middlewareData$trans.transformOrigin
    },
    arrowStyle: {
      left: (_middlewareData$arrow = middlewareData.arrow) === null || _middlewareData$arrow === void 0 ? void 0 : _middlewareData$arrow.x,
      top: (_middlewareData$arrow2 = middlewareData.arrow) === null || _middlewareData$arrow2 === void 0 ? void 0 : _middlewareData$arrow2.y
    },
    isHideTransition: hasAnimation && animationStatus === ANIMATION_STATUS.LEAVE,
    onTransitionEnd: handleTransitionEnd
  }));
}

export { TooltipCore as default };
